// The analysis lane on the CHART surface (Sep 2026, user: "show the analysis
// on the chart surface too"). The score draws its lane under each system;
// a chart has no systems, only cells, so the same analysis lands as a small
// roman under each chord symbol — the key prefix at a region's first chord,
// and cadence cells as an under-bracket built chord by chord (the first
// chord's mark opens it, the last closes it, so a cell that crosses a bar
// simply continues in the next cell). The theory is analysis.ts unchanged;
// this only keys it to the chords a ChartGrid body renders — by measure id
// and chord index, which both the chord score and the diagram sheet have.

import { analyzeHarmony, type HarmonicCellKind, type KeyRegion } from '@/music/analysis';
import { flatMeasuresToSequence, sequenceChordIndex } from '@/music/chart';
import type { IRealChart } from '@/music/ireal/types';

export interface ChartAnalysisMark {
  roman: string;
  /** "A♭:" / "g:" at the first chord of a key region. */
  keyPrefix?: string;
  /** Where in a cadence cell this chord sits, for the under-bracket. */
  cell?: 'only' | 'start' | 'mid' | 'end';
  cellKind?: HarmonicCellKind;
}

/** The mark key a ChartGrid body can form for the chord it is drawing: the
 * chord's index in `measure.chords`. A sheet of VOICED chords gets it from the
 * chord's id (`sequenceChordIndex`), never from its position in the sheet. */
export const chartMarkKey = (measureId: string, chordIndex: number) => `${measureId}:${chordIndex}`;

/** Major keys read as-is ("A♭:"), minor lowercase ("g:") — the lane's convention. */
const keyPrefix = (region: KeyRegion): string =>
  region.minor ? `${region.label.replace(/ minor$/, '').toLowerCase()}:` : `${region.label}:`;

export function chartAnalysisMarks(chart: IRealChart): ReadonlyMap<string, ChartAnalysisMark> {
  const marks = new Map<string, ChartAnalysisMark>();
  const sequence = flatMeasuresToSequence(chart.measures, 'middle', chart.timeSignature);
  if (sequence.length === 0) return marks;
  const analysis = analyzeHarmony(sequence, chart.key);
  const keys = sequence.map((entry) => {
    const ci = sequenceChordIndex(entry.id);
    const measure = chart.measures[entry.barIndex];
    return measure && ci != null ? chartMarkKey(measure.id, ci) : null;
  });
  analysis.romans.forEach((roman, i) => {
    const key = keys[i];
    if (key && roman) marks.set(key, { roman });
  });
  for (const region of analysis.regions) {
    const mark = keys[region.from] && marks.get(keys[region.from]!);
    if (mark) mark.keyPrefix = keyPrefix(region);
  }
  for (const cell of analysis.cells) {
    for (let i = cell.from; i <= cell.to; i++) {
      const mark = keys[i] && marks.get(keys[i]!);
      if (!mark) continue;
      mark.cellKind = cell.kind;
      mark.cell =
        cell.from === cell.to ? 'only' : i === cell.from ? 'start' : i === cell.to ? 'end' : 'mid';
    }
  }
  return marks;
}
